/**
 * Validation for the `redirect_path` destination carried across an auth transition.
 *
 * The value is attacker-supplied: it rides in the query string of any login URL, so a
 * crafted link can name whatever destination it likes and the app replays it once the
 * victim signs in. Everything here exists to keep that replay inside the app, and to keep
 * credentials out of a value that outlives the navigation carrying it — it lands in the
 * URL bar, in browser history, and in the session storage the OAuth hop reads back.
 *
 * The two policies are both denylists, for opposite reasons.
 *
 * Routes are a denylist because an allowlist fails in the direction of the bug this was
 * written for: every new authenticated route would silently stop resuming until someone
 * remembered to list it, and the symptom is "the link went to the directory" — exactly the
 * regression being fixed, reintroduced one route at a time. Once a path is known to be
 * relative and same-origin, the worst an attacker achieves is landing an already
 * authenticated user on a page they were entitled to visit. The targets that are not
 * merely useless are named below.
 *
 * Query parameters are a denylist because routes legitimately take bespoke ones, and an
 * allowlist would quietly drop a new parameter and break the destination it was meant to
 * reach.
 */

/** Query parameter naming the destination to resume after authentication. */
export const REDIRECT_PATH_PARAM = 'redirect_path';

/**
 * Route prefixes that must never be a destination.
 *
 * `/logout` is the one with teeth: named as the destination of a phished login link it
 * signs the victim straight back out, turning a fixed bug into a denial of sign-in. The
 * rest are auth screens, which as destinations loop back through the very transition
 * trying to resolve them.
 *
 * The last two are the secure screens a signed-in user is parked on until they finish
 * something. They loop for the same reason, and they reach here by a route the auth
 * screens do not: the destination is captured live rather than from a link, so a gate
 * that detours a user names itself as the place to resume. The symptom is not a loop but
 * a dead end — the user confirms, is sent back to the screen they just cleared, and the
 * destination that caused the detour is gone.
 *
 * Exported so that tightening this into an allowlist later is a change to one function
 * with the tests already standing.
 */
export const REDIRECT_DESTINATION_DENIED_PREFIXES = [
  '/login',
  '/logout',
  '/create-account',
  '/create-password',
  '/new-password',
  '/update-password',
  '/forgot-password',
  '/email-link',
  '/auth',
  '/redirect',
  '/confirm-account-details',
  '/npi-verification',
] as const;

/**
 * Query parameters dropped from a destination.
 *
 * These are credentials or identity. `redirect_path` itself is here as a nesting guard —
 * without it a capture folds any destination already in the URL back into the new one,
 * compounding on every pass until the query is longer than anything it describes.
 */
export const SENSITIVE_REDIRECT_PARAMS = [
  'access_token',
  'api_key',
  'apikey',
  'auth',
  'code',
  'confirmation_token',
  'email',
  'id_token',
  'invite_token',
  'key',
  'magic_token',
  'otp',
  'password',
  REDIRECT_PATH_PARAM,
  'refresh_token',
  'secret',
  'session',
  'sig',
  'signature',
  'state',
  'token',
] as const;

/** Router bookkeeping expo-router puts on every route; never part of a destination. */
const EXPO_ROUTER_PARAM_PREFIX = '__EXPO_ROUTER';

const SCHEME_PATTERN = /^[a-z][a-z0-9+.-]*:/i;

const CONTROL_OR_WHITESPACE_PATTERN = /[\u0000-\u0020\u007f]/;

const MAX_DECODE_PASSES = 5;

const sensitiveParamSet = new Set<string>(SENSITIVE_REDIRECT_PARAMS);

/**
 * Decodes until the value stops changing, so the checks run against what a router will
 * ultimately resolve rather than one layer of escaping above it. `%252F%252Fevil.com`
 * survives a single decode still looking like a path and arrives as `//evil.com`.
 *
 * A malformed escape sequence throws rather than decoding, and that is a rejection:
 * nothing legitimate reaches here malformed, and guessing at the author's intent is how a
 * bypass gets built.
 */
function fullyDecode(value: string): string | null {
  let current = value;

  for (let pass = 0; pass < MAX_DECODE_PASSES; pass += 1) {
    let next: string;

    try {
      next = decodeURIComponent(current);
    } catch {
      return null;
    }

    if (next === current) {
      return current;
    }

    current = next;
  }

  return null;
}

/** Splits `path?query` on the first `?`, having already dropped any fragment. */
function splitPathAndQuery(value: string): { pathname: string; query: string } {
  const withoutFragment = value.split('#')[0] ?? '';

  const queryStart = withoutFragment.indexOf('?');

  if (queryStart === -1) {
    return { pathname: withoutFragment, query: '' };
  }

  return {
    pathname: withoutFragment.slice(0, queryStart),
    query: withoutFragment.slice(queryStart + 1),
  };
}

function isDeniedRoute(pathname: string): boolean {
  const lowerPathname = pathname.toLowerCase();

  return REDIRECT_DESTINATION_DENIED_PREFIXES.some(
    (prefix) => lowerPathname === prefix || lowerPathname.startsWith(`${prefix}/`)
  );
}

/**
 * Whether a decoded `path?query` form is a relative in-app destination.
 *
 * `//host` is rejected because a protocol-relative URL reads as a path right up until a
 * browser resolves it against the current scheme and leaves the app.
 */
function isSafeForm(value: string): boolean {
  if (
    !value.startsWith('/') ||
    value.startsWith('//') ||
    SCHEME_PATTERN.test(value) ||
    CONTROL_OR_WHITESPACE_PATTERN.test(value)
  ) {
    return false;
  }

  const { pathname } = splitPathAndQuery(value);

  if (pathname.split('/').includes('..')) {
    return false;
  }

  return !isDeniedRoute(pathname);
}

function stripSensitiveParams(query: string): string {
  const survivors = new URLSearchParams();

  new URLSearchParams(query).forEach((value, key) => {
    if (sensitiveParamSet.has(key.toLowerCase()) || key.startsWith(EXPO_ROUTER_PARAM_PREFIX)) {
      return;
    }

    survivors.append(key, value);
  });

  return survivors.toString();
}

/**
 * Returns a destination safe to store and to replay, or `null` when the value cannot be
 * trusted.
 *
 * The shape is validated against the fully decoded form so escaping cannot smuggle a
 * hostile shape past the checks, while the value returned is rebuilt from a single decode
 * — a path segment that legitimately contains an encoded character survives that way, and
 * the router receives what the link actually named.
 *
 * Called at both ends deliberately. Validating at capture keeps a hostile value from being
 * written down at all; validating at replay covers the values that arrive from somewhere
 * other than a capture, notably the OAuth round trip, which reads back from session
 * storage that same-origin script can write.
 */
export function sanitizeRedirectDestination(raw: string | null | undefined): string | null {
  if (!raw) {
    return null;
  }

  const trimmed = raw.trim();

  if (!trimmed || trimmed.includes('\\')) {
    return null;
  }

  const fullyDecoded = fullyDecode(trimmed);

  if (fullyDecoded === null || fullyDecoded.includes('\\') || !isSafeForm(fullyDecoded)) {
    return null;
  }

  let singlyDecoded: string;

  try {
    singlyDecoded = decodeURIComponent(trimmed);
  } catch {
    return null;
  }

  if (!isSafeForm(singlyDecoded)) {
    return null;
  }

  const { pathname, query } = splitPathAndQuery(singlyDecoded);

  /*
   * A query separator can be hidden behind extra encoding: `%253F` reads as plain path
   * text after one decode and only becomes `?` once fully decoded. Shape validation above
   * already ran against the fully decoded form, so it would pass a value whose real query
   * this split can't see — and a query it can't see is a query `stripSensitiveParams`
   * never runs on. Failing closed on that mismatch is what keeps the two checks looking
   * at the same delimiter.
   */
  if (Boolean(query) !== Boolean(splitPathAndQuery(fullyDecoded).query)) {
    return null;
  }

  const safeQuery = stripSensitiveParams(query);

  return safeQuery ? `${pathname}?${safeQuery}` : pathname;
}
